import { useCallback } from "react";

import { useModals } from "hooks";
import { useDealActions, useDocumentsActions } from "store";
import { IFile } from "store/types";
import { IFileMetaDetail, IMetaDetails } from "store/types/deal/folder";

import { EntityType } from "../../../constants";
import { useWorkspace } from "../WorkspaceContext";

const isFileItem = (item: { type: string }): item is IFileMetaDetail =>
  item.type !== EntityType.DOCUMENT && item.type !== EntityType.FORM && item.type !== EntityType.FOLDER;

interface UseWorkspaceBulkActionsHookParams {
  selectedItems: IMetaDetails[];
  setSelectedItems: React.Dispatch<React.SetStateAction<IMetaDetails[]>>;
  useItemDealId?: boolean;
}

export const useWorkspaceBulkActionsHook = ({
  selectedItems,
  setSelectedItems,
  useItemDealId
}: UseWorkspaceBulkActionsHookParams) => {
  const { deleteModal } = useModals();
  const { downloadFileAction, deleteFormAction, deleteFileAction, deleteFolderAction } = useDealActions();
  const { deleteDocumentAction } = useDocumentsActions();
  const { dispatchDetail } = useWorkspace();

  const getItemDealId = useCallback(
    (item: IMetaDetails) => (useItemDealId ? ((item as IFileMetaDetail).deal?.id ?? null) : null) as number,
    [useItemDealId]
  );

  const handleBulkDelete = useCallback(() => {
    if (!selectedItems.length) return;

    const count = selectedItems.length;
    deleteModal({
      message: `Are you sure you want to delete ${count} selected ${count === 1 ? "item" : "items"}?`,
      action: async () => {
        const itemsToDelete = [...selectedItems];
        setSelectedItems([]);
        for (const item of itemsToDelete) {
          const itemDealId = getItemDealId(item);
          if (item.type === EntityType.FOLDER) {
            await deleteFolderAction({ dealId: itemDealId, folderId: item.id });
          } else if (item.type === EntityType.DOCUMENT) {
            await deleteDocumentAction(item.id);
          } else if (item.type === EntityType.FORM) {
            await deleteFormAction({ dealId: itemDealId, formId: item.id });
          } else if (isFileItem(item)) {
            await deleteFileAction({ id: item.id, deal_id: itemDealId } as IFile);
          }
          dispatchDetail({ key: "delete", id: item.id, entityType: item.entity_type });
        }
      }
    });
  }, [
    selectedItems,
    deleteModal,
    deleteDocumentAction,
    deleteFolderAction,
    deleteFormAction,
    deleteFileAction,
    setSelectedItems,
    dispatchDetail,
    getItemDealId
  ]);

  const handleBulkDownload = useCallback(async () => {
    const fileItems = selectedItems.filter(isFileItem);
    for (const item of fileItems) {
      const itemDealId = getItemDealId(item);
      await downloadFileAction({ fileId: item.id, fileName: item.name, dealId: itemDealId });
      await new Promise((resolve) => setTimeout(resolve, 300));
    }
  }, [selectedItems, downloadFileAction, getItemDealId]);

  return { handleBulkDelete, handleBulkDownload };
};
